// Today's date for the analytics chat's prompts. Without it a model reads
// "recent" against its training cutoff and spends an investigation querying
// the wrong year.
//
// Day granularity on purpose: the block rides in the system prompt, and a
// prompt that changed on every run would throw away the provider's cached
// prefix (system prompt plus transcript) on every turn. The day is the
// user's own calendar day, with the zone and UTC offset needed to write the
// UTC windows analytics_query takes.

import { setSystemPrompt } from "./agentTranscript.js";

const CURRENT_DATE_BLOCK = /\n*<current_date>[\s\S]*?<\/current_date>/;

function localTimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}

function calendarDay(now, timeZone) {
    const parts = {};
    const format = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "long",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZoneName: "longOffset",
    });
    for (const { type, value } of format.formatToParts(now)) parts[type] = value;
    // "GMT-07:00". ICU builds disagree on a zero offset: bare "GMT" in
    // Node 24, "GMT+00:00" in Node 26.
    const gmt = parts.timeZoneName === "GMT" ? "GMT+00:00" : parts.timeZoneName;
    return {
        weekday: parts.weekday,
        date: `${parts.year}-${parts.month}-${parts.day}`,
        offset: gmt.replace(/^GMT/, "UTC"),
    };
}

export function currentDateSection(now = new Date(), timeZone = localTimeZone()) {
    let zoneName = timeZone;
    let day;
    try {
        day = calendarDay(now, zoneName);
    } catch {
        // An unknown zone is a RangeError; UTC's day is still the right year.
        zoneName = "UTC";
        day = calendarDay(now, zoneName);
    }
    return [
        "<current_date>",
        `Today is ${day.weekday}, ${day.date} in the user's time zone (${zoneName}, ${day.offset}). Resolve "today", "yesterday", "last week" and dates written without a year against it, and build absolute windows from it, never from the year your training data suggests.`,
        "</current_date>",
    ].join("\n");
}

function replaceCurrentDateBlock(systemPrompt, section) {
    const prompt = String(systemPrompt || "");
    if (CURRENT_DATE_BLOCK.test(prompt)) return prompt.replace(CURRENT_DATE_BLOCK, `\n\n${section}`);
    return prompt ? `${prompt}\n\n${section}` : section;
}

// Stamps the block into a pi Agent's system prompt now, and again as every
// run starts. pi snapshots the prompt at run start, and a chat's agent lives
// as long as its tab, so a date written only at build time would still say
// yesterday after midnight.
export function stampCurrentDate(agent, section = () => currentDateSection()) {
    const stamp = () => {
        setSystemPrompt(agent, replaceCurrentDateBlock(agent.state.systemPrompt, section()));
    };
    stamp();
    for (const method of ["prompt", "continue"]) {
        const run = agent[method].bind(agent);
        agent[method] = (...args) => {
            stamp();
            return run(...args);
        };
    }
}
